import * as yup from 'yup';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, TextField } from '@mui/material';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ButtonCustom } from '../../../../../../components/elements/ButtonCustom';
import React, { useEffect } from 'react';
import { AddIcon } from '../../../../../../../assets/icon';
import { TextError } from '../../../../../../components/elements/TextError';

const schema = yup.object({
  clientCost: yup
    .array()
    .required()
    .of(
      yup.object({
        key: yup.string().required('Key is required'),
        value: yup.string().required('Value is required')
      })
    )
});

export type ClientCostItem = {
  key: string;
  value: string;
  document?: File | null;
};
type FormValues = {
  clientCost: ClientCostItem[];
};
type ModalPricingFormProps = {
  isOpen: boolean;
  onSubmit: (value: FormValues) => void;
  setOpen: (value: boolean) => void;
};
export const ModalPricingForm = ({ onSubmit, isOpen, setOpen }: ModalPricingFormProps) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      clientCost: [{ key: '', value: '', document: null }]
    },
    resolver: yupResolver(schema)
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'clientCost'
  });

  useEffect(() => {
    setValue('clientCost', [{ key: '', value: '', document: null }]);
  }, [isOpen]);

  return (
    <DialogConfirm
      width={'500px'}
      isOpen={isOpen}
      onClose={() => {
        setOpen(false);
      }}
      textTitle={'Add Cost'}>
      <Stack spacing={2} width={'100%'}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={'center'}
          spacing={2}
          width="100%">
          <Stack gap={1} width={'100%'} alignItems={'flex-end'}>
            <AddIcon
              onClick={() => append({ key: '', value: '', document: null })}
              width={'40px'}
              fill={'#fff'}
              color={'#22374A'}
            />
            {fields.map((_, index) => (
              <Stack width={'100%'} key={index} direction="row" gap={1} alignItems="center">
                <Stack gap={0.5} style={{ width: '250px' }}>
                  <Controller
                    name={`clientCost.${index}.key`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} variant="outlined" size="small" placeholder="Key" />
                    )}
                  />
                  {errors?.clientCost && errors?.clientCost[index]?.key?.message && (
                    <TextError errorText={errors?.clientCost[index]?.key?.message || ''} />
                  )}
                </Stack>
                <Stack style={{ width: '250px' }} gap={0.5}>
                  <Controller
                    name={`clientCost.${index}.value`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type={'number'}
                        variant="outlined"
                        size="small"
                        placeholder="Value"
                      />
                    )}
                  />
                  {errors?.clientCost && errors?.clientCost[index]?.value?.message && (
                    <TextError errorText={errors?.clientCost[index]?.value?.message || ''} />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <input
                    type="file"
                    hidden
                    id={`upload-${index}`}
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setValue(`clientCost.${index}.document`, file);
                    }}
                  />
                  <UploadFileIcon
                    style={{ cursor: 'pointer', color: '#22374A' }}
                    onClick={() => {
                      const input = document.getElementById(`upload-${index}`) as HTMLInputElement;
                      input?.click();
                    }}
                  />
                  {watch(`clientCost.${index}.document`) && (
                    <span
                      style={{
                        width: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '12px',
                        color: '#22374A'
                      }}>
                      {control._formValues.clientCost[index].document.name}
                    </span>
                  )}
                </Stack>
                <DeleteIcon onClick={() => remove(index)} />
              </Stack>
            ))}
            <ButtonCustom
              style={{ width: 120, height: 36 }}
              onClick={handleSubmit(onSubmit)}
              label={'Save'}
            />
          </Stack>
        </Stack>
      </Stack>
    </DialogConfirm>
  );
};
